import { StrictMode } from 'react'
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react'
import { EventType, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { AppInsightsContext, AppInsightsErrorBoundary, ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { createBrowserHistory } from "history"
import { initAppUrl } from '@/services/app.ts'

import "@cagovweb/state-template/dist/css/cagov.core.css"
import "@cagovweb/state-template/dist/css/colortheme-santacruz.css"
import "@cagovweb/state-template/dist/js/cagov.core.js"
import 'bootstrap-icons/font/bootstrap-icons.css'
import "@progress/kendo-theme-bootstrap/dist/all.css"
import '@/styles/index.css'

import Loader from '@/components/shared/Loader'
import InactivityHandler from '@/components/layout/InactivityHandler.tsx'

// Import the generated route tree
import { routeTree } from '@/routeTree.gen'
import { GlobalStateModel } from '@/models.ts'

// Create a new router instance
const router = createRouter({
    routeTree,
    defaultPendingComponent: () => <Loader containerClass="py-4 spinner-navy" />,
    context: {} as GlobalStateModel,
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router
    }
}

export async function RenderApp(globalState: GlobalStateModel) {
    globalState.queryClient = new QueryClient()
    // render(<App globalState={globalState} />, document.getElementById('app'))

    if (import.meta.env.VITE_ADMIN) {
      console.log(`Frontend admin application`);
    } else
    {
      console.log(`Public web application`);
    }

    // MSAL configuration
    var redirectUri = new URL('/msalLogin', window.location.href)
    //redirectUri.searchParams.append("state", window.location.pathname)
    const msalConfig = {
        auth: {
            clientId: globalState.azureAD.ClientId,
            authority: globalState.azureAD.Authority,
        //authority: `${globalState.azureAD.Instance}${globalState.azureAD.TenantId}`,
            redirectUri: redirectUri.href,
        },
        cache: {
            cacheLocation: "localStorage", // This configures where your cache will be stored
            storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
        },
        system: {
            loggerOptions: {
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Info:
                            // Note: turned off MSAL console info statements to keep browser console decluttered while debugging
                            // console.info(message);
                            return;
                        case LogLevel.Verbose:
                            // console.debug(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                        default:
                            return;
                    }
                }
            }
        }
    }
    globalState.pca = new PublicClientApplication(msalConfig)
    await globalState.pca.initialize()

    const otherTenantAccounts = globalState.pca.getAllAccounts()
        .filter(account => account.tenantId !== globalState.azureAD.TenantId)
    for (const account of otherTenantAccounts) {
        await globalState.pca.clearCache({ account })
    }

    globalState.pca.addEventCallback(event => {
        if (event.eventType === EventType.LOGIN_SUCCESS) {
            localStorage.setItem("timeLastActive", new Date().toISOString())
        }
    })

    globalState.hasRole = () => false
    globalState.editFilePermissions = () => []

    const browserHistory = createBrowserHistory()
    var reactPlugin = new ReactPlugin()
    // App Insights configuration: expected for non-Development environments but optional in Development
    if (globalState.env !== 'Development' || globalState.appInsightsConnectionString !== 'InstrumentationKey=') {
        var appInsights = new ApplicationInsights({
            config: {
                connectionString: globalState.appInsightsConnectionString,
                enableAutoRouteTracking: true,
                // *** If you're adding the Click Analytics plug-in, delete the next line. ***
                extensions: [reactPlugin],
                // *** Add the Click Analytics plug-in. ***
                // extensions: [reactPlugin, clickPluginInstance],
                extensionConfig: {
                    [reactPlugin.identifier]: { history: browserHistory }
                    // *** Add the Click Analytics plug-in. ***
                    // [clickPluginInstance.identifier]: clickPluginConfig
                }
            }
        })
        appInsights.loadAppInsights()
    }

    // Render the app
    const rootElement = document.getElementById('app')!
    if (!rootElement.innerHTML) {
        const root = ReactDOM.createRoot(rootElement)
        root.render(
            <StrictMode>
                <AppInsightsErrorBoundary onError={() => <h1>I believe something went wrong</h1>} appInsights={reactPlugin}>
                    <AppInsightsContext.Provider value={reactPlugin}>
                        <MsalProvider instance={globalState.pca}>
                            <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
                                <QueryClientProvider client={globalState.queryClient}>
                                    <RouterProvider router={router} context={globalState} />
                                    <InactivityHandler globalState={globalState} />
                                    <ReactQueryDevtools initialIsOpen={false} />
                                </QueryClientProvider>
                            </MsalAuthenticationTemplate>
                        </MsalProvider>
                    </AppInsightsContext.Provider>
                </AppInsightsErrorBoundary>
            </StrictMode>,
        )
    }
}

window.RenderApp = RenderApp
